import { Component, Emit, Prop, Ref, Vue } from 'vue-property-decorator'
import PatientSearcherComponent from '@/components/common/searcher/components/patient/patient-searcher.vue'
import PatientSearcher, { initialPatientIdentifierTypeCode } from '@/components/common/searcher/components/patient/patient-searcher'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { billingCommonCustomerService } from '@/api/services/billing/common-customer/billing-common-customer.service'
import { CustomerResponse } from '@/api/services/billing/common-customer/billing-common-customer.dto'
import { PatientSearcherRequestParams } from '@/components/common/searcher/components/patient/patient-searcher.model'
import { IdentifierTypeResponse } from '@/api/resources/common/initialization/common-initialization.dto'
import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { commonInitializationService } from '@/api/resources/common/initialization/common-initialization.service'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { GridUtils } from '@/shared/util/grid-utils'
import i18n from '@/lang/i18n'
import { StrictNullable } from '@/shared/types/app.types'
import { SearcherStatus } from '@/components/common/searcher/shared/models/common-searcher.model'
import { store } from '@/store/store'

export function initialCustomerIdentifierTypeCode(): string {
    return initialPatientIdentifierTypeCode()
}
export async function getCustomerIdentifierTypes(): Promise<IdentifierTypeResponse[]> {
    const response = await PromiseUtils.toCallSecure(commonInitializationService.getIdentifierTypes())
    if (response) {
        const identifierTypeNIF =
            response.find(identifierType => identifierType.code === initialCustomerIdentifierTypeCode())
        if (identifierTypeNIF) {
            identifierTypeNIF.name = i18n.tc('configuration.modal.client.identifier')
            return [identifierTypeNIF]
        }
    }
    return []
}

@Component({
    components: {
        PatientSearcherComponent
    }
})
export default class BillingModalCustomerSelectionSearcher extends Vue {
    @Prop({ type: String, default: 'customer' }) readonly qaId!: string
    @Prop({ type: Boolean, default: true }) readonly autofocus!: boolean

    @Prop({ type: Function, default: getCustomerIdentifierTypes }) readonly getCustomerIdentifierTypes!: () => Promise<IdentifierTypeResponse[]>
    @Prop({ type: String, default: initialCustomerIdentifierTypeCode }) readonly initialCustomerIdentifierTypeCode!: keyof IdentifierTypeResponse['code']

    @Ref() patientSearcher!: PatientSearcher<CustomerResponse>

    @Emit() // from patient searcher
    searchStatus(status: SearcherStatus): SearcherStatus {
        return status
    }
    @Emit() // from patient searcher
    searched(searchedData: CustomerResponse[]): CustomerResponse[] {
        return searchedData
    }
    @Emit()
    identifierTypeChanged(identifierType: StrictNullable<IdentifierTypeResponse>): StrictNullable<IdentifierTypeResponse> {
        return identifierType
    }

    ///

    focus() {
        if (this.patientSearcher) {
            this.patientSearcher.focus()
        }
    }

    async searcher(searcherParams: PatientSearcherRequestParams): Promise<CustomerResponse[]> {
        const { identifierType, id, name } = searcherParams
        this.identifierTypeChanged(identifierType)

        const filterModel: CommonFilterModel = {}
        if (id) {
            filterModel.taxIdentificationNumber = GridUtils.getTextFilterModel('contains', id)
        }
        if (name) {
            filterModel.name = GridUtils.getTextFilterModel('contains', name)
        }

        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }

        const response =
            await PromiseUtils.toCallSecure(
                billingCommonCustomerService.paginator(
                    filterModel,
                    params,
                    store.state.labId))
        return response?.content ?? []
    }
}
